import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSidebar } from '@contexts/useSidebar';
import MessageBox from '@components/2-molecules/MessageBox';
import AppTopBar from '@components/AppTopBar';
import AppsFooter from '@components/AppsFooter';
import {
  Wrapper,
  Content,
  Sidebar,
  Main,
  MainContent,
  Backdrop,
  BannerWrapper,
} from './BaseLayout.styled';

// These pages render their own TopBar (with the avatar), so the global one would duplicate it.
const ROUTES_WITHOUT_APP_TOP_BAR = ['/processes'];

const ownsTopBar = (pathname) =>
  ROUTES_WITHOUT_APP_TOP_BAR.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

const isEditorRoute = (pathname) =>
  pathname.startsWith('/processes/') && pathname !== '/processes/';

const BaseLayout = ({ banners, children }) => {
  const { isMobileOpen, closeMobile, isEditorRailExpanded, isNavCollapsed } = useSidebar();
  const { pathname } = useLocation();
  const showAppTopBar = !ownsTopBar(pathname);
  const narrowSidebar = isEditorRoute(pathname) ? !isEditorRailExpanded : isNavCollapsed;

  return (
    <Wrapper style={{ height: '100%' }}>
      {banners?.map((banner) => (
        <BannerWrapper key={banner.key}>
          <MessageBox
            key={banner.key}
            leadingIconName={banner.leadingIconName}
            endingButtonTitle={banner.endingButtonTitle}
            endingButtonIconName={banner.endingButtonIconName}
            endingIconButtonIconName={banner.endingIconButtonIconName}
            onEndingButtonClick={banner.onEndingButtonClick}
            onEndingIconButtonClick={banner.onEndingIconButtonClick}
            variant={banner.variant}
            noBorderRadius
          >
            {banner.contentText}
          </MessageBox>
        </BannerWrapper>
      ))}

      <Content $narrow={narrowSidebar} data-narrow={narrowSidebar || undefined}>
        <Backdrop $visible={isMobileOpen} onClick={closeMobile} />

        <Sidebar $mobileOpen={isMobileOpen} $rail={narrowSidebar}>
          {children?.[0] || null}
        </Sidebar>

        <Main>
          {showAppTopBar && <AppTopBar />}
          <MainContent>{children?.[1] || null}</MainContent>
          <AppsFooter />
        </Main>
      </Content>
    </Wrapper>
  );
};

BaseLayout.propTypes = {
  banners: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
};

export default BaseLayout;
